import React from "react";
import { useI18n } from "../i18n";
import { useSettings } from "../contexts/SettingsContext";

const SellerTerms = () => {
  const { lang } = useI18n();
  const { settings } = useSettings();
  const commission = settings?.marketplace?.default_commission_pct ?? 15;
  const isNL = lang === "nl";

  return (
    <div className="max-w-[820px] mx-auto px-6 py-16" data-testid="seller-terms-page">
      <div className="font-label text-ink-muted">{isNL ? "JURIDISCH" : "LEGAL"}</div>
      <h1 className="font-display text-5xl tracking-tighter font-light">{isNL ? "Algemene Voorwaarden voor Verkopers" : "Seller Terms & Conditions"}</h1>
      <p className="text-ink-muted mt-2 text-sm">{isNL ? "Laatst bijgewerkt" : "Last updated"}: 11 February 2026</p>

      <div className="prose prose-sm max-w-none mt-10 leading-relaxed space-y-6 text-ink">
        <Sec n="1" t={isNL ? "Toepasbaarheid" : "Applicability"}>
          {isNL
            ? "Deze voorwaarden gelden voor alle verkopers die producten aanbieden via SnapShop.it.com (\"SnapShop\"). Door een verkopersaanvraag in te dienen, ga je akkoord met deze voorwaarden."
            : "These terms apply to all sellers offering products through SnapShop.it.com (\"SnapShop\"). By submitting a seller application, you agree to these terms."}
        </Sec>
        <Sec n="2" t={isNL ? "Verkopersregistratie & verificatie" : "Seller registration & verification"}>
          {isNL ? (
            <ul className="list-disc pl-5 space-y-1">
              <li>Verkopers moeten een geldig Nederlands KvK-nummer en BTW-nummer overleggen.</li>
              <li>Een geldig IBAN op naam van het bedrijf is verplicht voor uitbetaling.</li>
              <li>SnapShop beoordeelt elke aanvraag binnen 1-2 werkdagen en kan aanvragen afwijzen zonder opgaaf van reden.</li>
              <li>SnapShop kan documenten opvragen om je bedrijf te verifiëren (KvK-uittreksel, ID, etc.).</li>
            </ul>
          ) : (
            <ul className="list-disc pl-5 space-y-1">
              <li>Sellers must provide a valid Dutch KvK number and BTW (VAT) number.</li>
              <li>A valid IBAN registered to the company is required for payouts.</li>
              <li>SnapShop reviews each application within 1-2 business days and may reject applications without reason.</li>
              <li>SnapShop may request documents to verify your business (KvK extract, ID, etc.).</li>
            </ul>
          )}
        </Sec>
        <Sec n="3" t={isNL ? "Productvereisten — geen dropshipping" : "Product requirements — no dropshipping"}>
          {isNL
            ? "Verkopers moeten eigen voorraad houden of een aantoonbare leveringsketen hebben. Pure dropshipping vanuit verre marktplaatsen is verboden. SnapShop kan op elk moment voorraadbewijs opvragen."
            : "Sellers must hold their own stock or have a verifiable supply chain. Pure dropshipping from distant marketplaces is prohibited. SnapShop may request stock proof at any time."}
        </Sec>
        <Sec n="4" t={isNL ? "Commissie" : "Commission"}>
          {isNL
            ? `SnapShop rekent standaard ${commission}% commissie over de verkoopprijs (inclusief BTW, exclusief verzendkosten). Een afwijkend tarief kan per verkoper of categorie worden afgesproken en wordt in je dashboard getoond. De commissie is opgenomen in de orderregels en wordt automatisch berekend bij elke verkoop.`
            : `SnapShop charges a standard ${commission}% commission on the sale price (incl. VAT, excl. shipping). A different rate can be agreed per seller or category and is displayed in your dashboard. Commission is recorded per order line and calculated automatically on every sale.`}
        </Sec>
        <Sec n="5" t={isNL ? "Uitbetalingen" : "Payouts"}>
          {isNL
            ? "Uitbetalingen vinden plaats volgens het Net 30 schema: alle betaalde orders van de voorafgaande maand worden in de eerste week van de volgende maand uitbetaald op het IBAN van de verkoper. Tijdens Fase 1 zijn uitbetalingen handmatig. Mollie Connect (automatische splitsing) wordt later geïntegreerd."
            : "Payouts follow a Net 30 schedule: all paid orders from the previous month are paid out in the first week of the following month to the seller's IBAN. During Phase 1 payouts are manual. Mollie Connect (auto-split) will be integrated later."}
        </Sec>
        <Sec n="6" t={isNL ? "Verzending & retouren" : "Shipping & returns"}>
          {isNL
            ? "Eén verzend- en retourbeleid geldt voor alle verkopers: PostNL/DHL standaardtarieven en 30 dagen retour. Verkopers verzenden binnen 1 werkdag na betaling. Retour-kosten worden door SnapShop verwerkt en bij de verkoper in mindering gebracht op de eerstvolgende uitbetaling."
            : "A single shipping and returns policy applies to all sellers: PostNL/DHL standard rates and 30-day returns. Sellers ship within 1 business day of payment. Return costs are processed by SnapShop and deducted from the next seller payout."}
        </Sec>
        <Sec n="7" t={isNL ? "BTW & facturatie" : "VAT & invoicing"}>
          {isNL
            ? "SnapShop genereert namens elke verkoper BTW-conforme facturen (KvK + BTW van de verkoper op de factuur). De verkoper blijft volledig verantwoordelijk voor zijn eigen BTW-aangifte richting de Belastingdienst."
            : "SnapShop generates BTW-compliant invoices on behalf of each seller (with the seller's KvK + BTW on the invoice). The seller remains fully responsible for their own VAT filings with the Dutch tax authority."}
        </Sec>
        <Sec n="8" t={isNL ? "Schorsing & beëindiging" : "Suspension & termination"}>
          {isNL ? (
            <>
              SnapShop kan een verkopersaccount onmiddellijk schorsen of beëindigen bij:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Schending van deze voorwaarden of het marketplace beleid.</li>
                <li>Herhaalde negatieve recensies of klantklachten.</li>
                <li>Vermoeden van fraude, vervalste producten of dropshipping.</li>
                <li>Niet leveren binnen de toegezegde termijn.</li>
              </ul>
            </>
          ) : (
            <>
              SnapShop may immediately suspend or terminate a seller account for:
              <ul className="list-disc pl-5 mt-2 space-y-1">
                <li>Violation of these terms or the marketplace policy.</li>
                <li>Repeated negative reviews or customer complaints.</li>
                <li>Suspicion of fraud, counterfeit goods, or dropshipping.</li>
                <li>Failure to ship within the committed time frame.</li>
              </ul>
            </>
          )}
        </Sec>
        <Sec n="9" t={isNL ? "Aansprakelijkheid" : "Liability"}>
          {isNL
            ? "De verkoper is volledig aansprakelijk voor de producten die hij aanbiedt: productveiligheid, juiste beschrijving, garantie en consumentenrecht. SnapShop fungeert als platform en niet als verkoper voor producten van derden."
            : "The seller is fully liable for the products they offer: product safety, accurate description, warranty, and consumer rights. SnapShop acts as a platform and not as the seller of third-party products."}
        </Sec>
        <Sec n="10" t={isNL ? "Wijzigingen" : "Changes"}>
          {isNL
            ? "SnapShop kan deze voorwaarden wijzigen. Verkopers worden minimaal 14 dagen voor inwerkingtreding van wezenlijke wijzigingen per e-mail geïnformeerd."
            : "SnapShop may update these terms. Sellers will be notified by email at least 14 days before material changes take effect."}
        </Sec>
        <Sec n="11" t={isNL ? "Toepasselijk recht" : "Governing law"}>
          {isNL
            ? "Op deze overeenkomst is Nederlands recht van toepassing. Geschillen worden voorgelegd aan de bevoegde rechter te Amsterdam."
            : "Dutch law applies. Disputes are submitted to the competent court of Amsterdam."}
        </Sec>
      </div>
    </div>
  );
};

const Sec = ({ n, t, children }) => (
  <section>
    <h2 className="font-display text-xl tracking-tight mb-2">{n}. {t}</h2>
    <div className="text-sm leading-relaxed">{children}</div>
  </section>
);

export default SellerTerms;
